import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { queryKeys } from '~/api/queryKeys';
import { useArchiveFeedback } from '~/features/list-archive/hooks/use-archive-feedback';

import { setExperimentArchived } from '../services/experiment.service';
import type { Experiment, ExperimentDetail } from '../types/experiment.types';

interface SetExperimentArchivedArgs {
  id: string;
  archived: boolean;
  /** Set on the single automatic retry after `ARCHIVE_STATE_CHANGED`. */
  retried?: boolean;
}

/** A refusal the UI must act on: a sub-run (pipeline / scoring / sample
 *  enrichment) is executing — `EXPERIMENT_WORK_IN_FLIGHT`. Remedy: Stop. */
export interface ExperimentArchiveRefusal {
  id: string;
}

/**
 * Archive / unarchive toggle for experiments. On success the response row's
 * attribution stamps are merged into the cached detail (`ExperimentDetail` is a
 * superset of the PATCH response) and `experiments.all` is invalidated so the
 * list, the Archived badge count and the existence probe refetch.
 *
 * Refusals: `ARCHIVE_STATE_CHANGED` re-reads and retries once, silently; an
 * in-flight sub-run is exposed as `refusal` for the owning control to open the
 * Stop dialog; everything else toasts.
 */
export function useSetExperimentArchived() {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useArchiveFeedback();
  const [refusal, setRefusal] = useState<ExperimentArchiveRefusal | null>(null);

  const mutation = useMutation<Experiment, unknown, SetExperimentArchivedArgs>({
    mutationFn: ({ id, archived }) => setExperimentArchived(id, archived),
    onSuccess: (row, { id, archived }) => {
      const stamps = {
        archivedAt: row.archivedAt ?? null,
        archivedBy: row.archivedBy ?? null,
        ...(row.lastActionBy != null ? { lastActionBy: row.lastActionBy } : {}),
        ...(row.lastActionAt != null ? { lastActionAt: row.lastActionAt } : {}),
      };
      queryClient.setQueryData<ExperimentDetail>(queryKeys.experiments.detail(id), (prev) =>
        prev ? { ...prev, ...stamps } : prev,
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.experiments.all });
      setRefusal(null);
      notifySuccess(
        archived,
        archived
          ? () => {
              mutation.mutate({ id, archived: false });
            }
          : undefined,
      );
    },
    onError: (err, { id, archived, retried }) => {
      const refused = notifyError(err);
      if (refused.kind === 'state-changed' && !retried) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.experiments.all });
        mutation.mutate({ id, archived, retried: true });
        return;
      }
      // `experiment-in-flight` is the contract's code; a legacy 409 without a
      // code classifies as `still-live` and means the same thing here.
      const inFlight =
        refused.kind === 'experiment-in-flight' ||
        (refused.kind === 'still-live' && refused.liveness !== 'UNKNOWN');
      if (inFlight && archived) {
        setRefusal({ id });
        return;
      }
      if (refused.kind === 'not-found') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.experiments.all });
      }
    },
  });

  return {
    /** Archives the experiment. Resolves on 200 and rejects on failure (already
     *  toasted, or handed to `refusal`) so a confirm popover can close itself. */
    archive: (id: string) => mutation.mutateAsync({ id, archived: true }),
    /** Unarchives the experiment — fire-and-forget; never refused by the server. */
    unarchive: (id: string) => {
      mutation.mutate({ id, archived: false });
    },
    /** Id of the experiment whose toggle is in flight, for a per-row spinner. */
    pendingId: mutation.isPending ? mutation.variables.id : null,
    /** Set when the last archive was refused because a sub-run is executing. */
    refusal,
    dismissRefusal: () => {
      setRefusal(null);
    },
  };
}
